import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Loader2, Megaphone, Plus } from "lucide-react";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TooltipProvider } from "@/components/ui/tooltip";
import { auth } from "@/lib/firebase";
import { projectsApi } from "@/lib/api/projects";

import { BroadcastFormDialog, WorkspaceProjectOption } from "./broadcast-form-dialog";
import { duplicateBroadcastDto } from "./broadcast-duplicate";
import { BroadcastRow } from "./broadcast-row";
import { RunHistoryDialog } from "./run-history-dialog";
import { TablePagination } from "./table-pagination";
import {
  BROADCAST_PAGE_SIZES,
  BROADCAST_RETENTION_DAYS,
  CreateBroadcastDto,
  DEFAULT_BROADCAST_PAGE_SIZE,
  Broadcast,
  broadcastsApi,
  useBroadcasts,
} from "./use-broadcasts";

/**
 * Which broadcasts this list shows, and therefore what it lets you do.
 *
 * - `workspace` — every broadcast in the workspace; create picks N projects.
 * - `project` — only the broadcasts that capture this project; create is pre-scoped to it, and a
 *   broadcast shared with other projects is read-only here (see BroadcastRow's `scope`).
 */
export type BroadcastsScope =
  | { kind: "workspace" }
  | { kind: "project"; projectId: string };

interface BroadcastsListProps {
  scope: BroadcastsScope;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (error as { response?: { data?: { message?: unknown } } }).response
      ?.data?.message === "string"
  ) {
    return (error as { response: { data: { message: string } } }).response.data
      .message;
  }
  return fallback;
}

const COPY = {
  workspace: {
    title: "Broadcasts",
    subtitle:
      "Capture one or many projects on a schedule and deliver them to Slack, Teams, email, a webhook, or PDF.",
    emptyTitle: "No broadcasts yet",
    emptyBody:
      "Set one schedule across several projects and deliver the snapshots where your team works.",
  },
  project: {
    title: "Broadcasts",
    subtitle:
      "Deliver a snapshot of this project to Slack, Teams, email, a webhook, or PDF on a schedule.",
    emptyTitle: "No broadcasts for this project",
    emptyBody:
      "Schedule one here, or add this project to an existing broadcast from workspace Broadcast.",
  },
} as const;

/**
 * The broadcasts list — shared by the workspace page and the per-project tab. Both surfaces read
 * the SAME server truth (one paged request, filtered server-side when scoped) and hold no
 * mirrored state, so a broadcast edited in one place is correct in the other on next load.
 */
export function BroadcastsList({ scope }: BroadcastsListProps): JSX.Element {
  const isProject = scope.kind === "project";
  const copy = isProject ? COPY.project : COPY.workspace;

  const [showFormDialog, setShowFormDialog] = useState(false);
  const [editingBroadcast, setEditingBroadcast] = useState<Broadcast | null>(null);
  const [historyBroadcast, setHistoryBroadcast] = useState<Broadcast | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [projects, setProjects] = useState<WorkspaceProjectOption[]>([]);

  // Previous / Next paging, the same footer OptiSigns uses.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_BROADCAST_PAGE_SIZE);

  const { broadcasts, loading, error, hasMore, total, usage, refresh } = useBroadcasts(
    isProject ? { projectId: scope.projectId } : {},
    { limit: pageSize, offset: (page - 1) * pageSize },
  );

  // The last broadcast of the last page was deleted: step back rather than show an empty page
  // whose footer still reads "3 / 2".
  useEffect(() => {
    if (!loading && !error && page > 1 && broadcasts.length === 0) setPage(page - 1);
  }, [loading, error, page, broadcasts.length]);

  // The multi-project picker is workspace-only; a project tab already knows its source.
  // Show ALL projects there — unpublished ones render disabled rather than vanishing.
  useEffect(() => {
    if (isProject) return;
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const token = await auth.getIdToken();
        const all = await projectsApi.getAll(token!);
        if (cancelled) return;
        setProjects(
          all.map((p) => ({
            id: p.id,
            name: p.name,
            published: Boolean(p.latestPublishUrl),
          })),
        );
      } catch (err) {
        console.error("Failed to load projects:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isProject]);

  const handleSubmit = async (
    dto: CreateBroadcastDto,
    opts?: { test?: boolean },
  ): Promise<void> => {
    try {
      if (editingBroadcast) {
        await broadcastsApi.updateBroadcast(editingBroadcast.id, dto);
        toast.success("Broadcast updated");
      } else {
        const created = await broadcastsApi.createBroadcast(dto);
        if (opts?.test) {
          await broadcastsApi.runNow(created.id);
          toast.success("Broadcast created — test sent, check the channel shortly");
        } else {
          toast.success("Broadcast created — use Run now to preview it");
        }
      }
      await refresh();
    } catch (err) {
      console.error("Failed to save broadcast:", err);
      toast.error(getErrorMessage(err, "Failed to save broadcast"));
      throw err;
    }
  };

  const handleRunNow = async (broadcast: Broadcast): Promise<boolean> => {
    try {
      await broadcastsApi.runNow(broadcast.id);
      toast.success("Run started — check run history in a minute");
      return true;
    } catch (err) {
      console.error("Failed to start test send:", err);
      toast.error(getErrorMessage(err, "Failed to start test send"));
      return false;
    }
  };

  const handleTogglePause = async (broadcast: Broadcast): Promise<void> => {
    try {
      await broadcastsApi.updateBroadcast(broadcast.id, { enabled: !broadcast.enabled });
      toast.success(broadcast.enabled ? "Broadcast paused" : "Broadcast resumed");
      await refresh();
    } catch (err) {
      console.error("Failed to update broadcast:", err);
      toast.error(getErrorMessage(err, "Failed to update broadcast"));
    }
  };

  const handleDelete = async (broadcast: Broadcast): Promise<void> => {
    try {
      await broadcastsApi.deleteBroadcast(broadcast.id);
      toast.success("Broadcast deleted");
      await refresh();
    } catch (err) {
      console.error("Failed to delete broadcast:", err);
      toast.error(getErrorMessage(err, "Failed to delete broadcast"));
      throw err;
    }
  };

  const handleEdit = (broadcast: Broadcast): void => {
    setEditingBroadcast(broadcast);
    setShowFormDialog(true);
  };

  const handleDuplicate = async (broadcast: Broadcast): Promise<void> => {
    try {
      await broadcastsApi.createBroadcast(duplicateBroadcastDto(broadcast));
      toast.success("Broadcast duplicated (paused)");
      // The copy is newest, so it lands on page 1.
      if (page === 1) await refresh();
      else setPage(1);
    } catch (err) {
      console.error("Failed to duplicate broadcast:", err);
      toast.error(getErrorMessage(err, "Failed to duplicate broadcast"));
    }
  };

  const handleOpenHistory = (broadcast: Broadcast): void => {
    setHistoryBroadcast(broadcast);
    setShowHistory(true);
  };

  const openCreate = (): void => {
    setEditingBroadcast(null);
    setShowFormDialog(true);
  };

  // Delivered-message deep link: the API stamps
  // `/dashboard/settings/broadcasts?broadcastId={{broadcastId}}&view=edit` into every
  // message channel and the engine fills the id per delivery, so the email/Slack/Teams button
  // ("Update broadcast settings") opens THIS broadcast's edit form; `view=history` opens its runs sheet (same shape as OptiSigns).
  // Fetched by id — the row may sit on a page the list has not loaded. Consumed once, then the
  // query is stripped so refresh/back do not re-open it.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkId = searchParams.get("broadcastId");
  const deepLinkView = searchParams.get("view");
  const handledDeepLinkRef = useRef<string | null>(null);
  useEffect(() => {
    if (isProject || !deepLinkId || handledDeepLinkRef.current === deepLinkId) return;
    handledDeepLinkRef.current = deepLinkId;
    let cancelled = false;
    void broadcastsApi
      .getBroadcast(deepLinkId)
      .then((broadcast) => {
        if (cancelled) return;
        if (deepLinkView === "edit") handleEdit(broadcast);
        else handleOpenHistory(broadcast);
      })
      .catch(() => {
        if (!cancelled) toast.error("Broadcast not found");
      })
      .finally(() => {
        if (!cancelled) setSearchParams({}, { replace: true });
      });
    return () => {
      cancelled = true;
    };
    // handleEdit / handleOpenHistory are stable setState wrappers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProject, deepLinkId, deepLinkView, setSearchParams]);

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4">
      {/* The project tab's shell already renders a "Broadcast" title + subtitle right
          above this list — repeating them here read as a duplicated header, so the
          scoped view keeps only the action (SCIO3's Broadcasts tab does the same). */}
      {isProject ? (
        <div className="flex items-center justify-end">
          <Button size="sm" data-testid="broadcasts-new-btn" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            New broadcast
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h4 className="text-sm font-semibold text-foreground">{copy.title}</h4>
            <p className="text-xs text-muted-foreground">{copy.subtitle}</p>
          </div>
          {/* Workspace-wide cap from app_configs.broadcastLimit; hidden when there is no cap. */}
          {typeof usage?.limit === "number" && (
            <span
              className="shrink-0 text-xs text-muted-foreground"
              data-testid="broadcasts-usage-counter"
            >
              {usage.enabled} of {usage.limit} on
            </span>
          )}
          <Button
            size="sm"
            className="shrink-0 whitespace-nowrap"
            data-testid="broadcasts-new-btn"
            onClick={openCreate}
          >
            <Plus className="mr-2 h-4 w-4" />
            New broadcast
          </Button>
        </div>
      )}

      {/* Full spinner only on first load; a page flip keeps the table (and footer) in place. */}
      {loading && broadcasts.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-border bg-muted/40 px-6 py-8 text-center">
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => void refresh()}
          >
            Retry
          </Button>
        </div>
      ) : broadcasts.length === 0 ? (
        <div
          className="rounded-xl border border-border bg-muted/40 px-6 py-12 text-center"
          data-testid="broadcasts-empty"
        >
          <Megaphone className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm font-medium text-foreground">
            {copy.emptyTitle}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{copy.emptyBody}</p>
          <Button className="mt-5" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            New broadcast
          </Button>
        </div>
      ) : (
        <TooltipProvider>
          {/* One flat ledger — SCIO3's Broadcasts-tab idiom: every fact in its column,
              status as a dot + words, actions revealed on row hover. */}
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <Table className="min-w-[800px]">
              <TableHeader>
                {/* SCIO3 ledger header: small caps, quiet, on a faintly tinted band. */}
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableHead className="h-10 text-[11px] font-semibold uppercase tracking-wide">
                    Name
                  </TableHead>
                  <TableHead className="h-10 text-[11px] font-semibold uppercase tracking-wide">
                    Send to
                  </TableHead>
                  <TableHead className="h-10 text-[11px] font-semibold uppercase tracking-wide">
                    Schedule
                  </TableHead>
                  <TableHead className="h-10 text-[11px] font-semibold uppercase tracking-wide">
                    Last run
                  </TableHead>
                  {/* Sticky on the right: on narrow screens the table scrolls sideways, and the
                      row's controls (pause, ⋮ menu) must stay reachable without scrolling. */}
                  <TableHead className="sticky right-[56px] z-10 h-10 w-[70px] bg-muted/40 text-[11px] font-semibold uppercase tracking-wide backdrop-blur">
                    Active
                  </TableHead>
                  <TableHead className="sticky right-0 z-10 h-10 w-[56px] bg-muted/40 backdrop-blur" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {broadcasts.map((broadcast) => (
                  <BroadcastRow
                    key={broadcast.id}
                    broadcast={broadcast}
                    scope={scope.kind}
                    onRunNow={handleRunNow}
                    onTogglePause={handleTogglePause}
                    onDelete={handleDelete}
                    onEdit={handleEdit}
                    onDuplicate={handleDuplicate}
                    onOpenHistory={handleOpenHistory}
                  />
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="pt-3">
            <TablePagination
              totalCount={total}
              pageSize={pageSize}
              currentPage={page}
              hasNextPage={hasMore}
              loading={loading}
              pageSizes={BROADCAST_PAGE_SIZES}
              onPageSizeChange={setPageSize}
              onPageChange={setPage}
            />
          </div>
        </TooltipProvider>
      )}

      <p className="pt-1 text-xs text-muted-foreground">
        Snapshots are stored for {BROADCAST_RETENTION_DAYS} days; older files are removed
        automatically.
      </p>

      <BroadcastFormDialog
        open={showFormDialog}
        onOpenChange={setShowFormDialog}
        mode={isProject ? "project" : "workspace"}
        projectId={isProject ? scope.projectId : undefined}
        projects={isProject ? undefined : projects}
        broadcast={editingBroadcast}
        onSubmit={handleSubmit}
      />

      <RunHistoryDialog
        open={showHistory}
        onOpenChange={setShowHistory}
        broadcast={historyBroadcast}
      />
    </div>
  );
}
